import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { API, setToken, getToken } from "@/lib/api";

const inputClass =
  "bg-[#14171A] border-[#A8AEB2]/25 text-white placeholder:text-[#A8AEB2]/60 focus-visible:ring-[#7AB648] focus-visible:ring-offset-0 h-12 rounded-sm";

export default function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If a token already exists, try to verify silently and skip login.
    const t = getToken();
    if (!t) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => nav("/admin", { replace: true }))
      .catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setToken(data.access_token);
      toast.success("Signed in");
      nav("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid email or password";
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#14171A] grain px-6"
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <Logo />
        </div>
        <div className="rounded-sm border border-[#A8AEB2]/15 bg-[#1E2227] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#7AB648]/15 text-[#7AB648]">
              <Lock size={16} />
            </span>
            <span className="font-body text-xs uppercase tracking-[0.35em] text-[#7AB648]">
              Admin Access
            </span>
          </div>
          <h1 className="font-display uppercase text-4xl text-white leading-[0.9]">
            Sign In
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label className="text-white font-body">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white font-body">Password</Label>
              <Input
                type="password"
                autoComplete="current-password"
                className={inputClass}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#7AB648] px-6 py-4 font-head font-bold uppercase tracking-wide text-[#14171A] transition-colors hover:bg-[#6BA339] active:scale-95 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>
        <div className="mt-6 text-center">
          <a
            href="/"
            className="font-body text-sm text-[#A8AEB2] hover:text-[#7AB648] transition-colors"
            data-testid="login-back-home"
          >
            &larr; Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
